import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { api } from '../api.js'
import { useAuth } from '../auth/AuthContext.jsx'

// Admin: discount create/edit — a standalone page (project rule: every form
// is its own page).
//
// A discount with a CODE has to be typed on the apply form; one without a
// code applies automatically to whoever matches. Leaving user / participant /
// course empty means "no restriction on that dimension"; filling several
// narrows the match (they are ANDed).
//
// Start date is REQUIRED. End date is optional — empty means the coupon
// never expires.

const EMPTY = {
  name: '', code: '', type: 'percent', value: '',
  applies_to: 'all', user_id: '', participant_id: '', course_id: '',
  max_uses: '', max_uses_per_user: '',
  valid_from: new Date().toISOString().slice(0, 10), valid_until: '',
  active: true,
}

export function AdminDiscountFormPage() {
  const { t } = useTranslation()
  const { id } = useParams()
  const navigate = useNavigate()
  const { user, loading: authLoading } = useAuth()
  const isEdit = Boolean(id)

  const [f, setF] = useState(EMPTY)
  const [users, setUsers] = useState([])
  const [courses, setCourses] = useState([])
  const [participants, setParticipants] = useState([])
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  const set = (k) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value
    setF((prev) => ({ ...prev, [k]: v }))
  }

  useEffect(() => {
    if (authLoading || !user || user.role !== 'admin') return
    const jobs = [api.admin.users.list(), api.admin.courses.list()]
    if (isEdit) jobs.push(api.admin.discounts.get(id))
    Promise.all(jobs)
      .then(([u, c, d]) => {
        setUsers(u.users || [])
        setCourses(c.courses || [])
        if (d?.discount) {
          const x = d.discount
          setF({
            name: x.name || '',
            code: x.code || '',
            type: x.type || 'percent',
            value: String(x.value ?? ''),
            applies_to: x.applies_to || 'all',
            user_id: x.user_id ? String(x.user_id) : '',
            participant_id: x.participant_id ? String(x.participant_id) : '',
            course_id: x.course_id ? String(x.course_id) : '',
            max_uses: x.max_uses ? String(x.max_uses) : '',
            max_uses_per_user: x.max_uses_per_user ? String(x.max_uses_per_user) : '',
            valid_from: x.valid_from || '',
            valid_until: x.valid_until || '',
            active: Number(x.active) === 1,
          })
        }
      })
      .catch((e) => setError(e.code || 'server'))
      .finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user, id, isEdit])

  // The participant picker only makes sense once an account is chosen.
  useEffect(() => {
    if (!f.user_id) { setParticipants([]); return }
    api.admin.users.get(f.user_id)
      .then((d) => setParticipants(d.participants || []))
      .catch(() => setParticipants([]))
  }, [f.user_id])

  async function submit(e) {
    e.preventDefault()
    setBusy(true); setError(null)
    const payload = {
      name: f.name.trim(),
      code: f.code.trim().toUpperCase(),
      type: f.type,
      value: f.value,
      applies_to: f.applies_to,
      user_id: f.user_id || null,
      participant_id: f.participant_id || null,
      course_id: f.course_id || null,
      max_uses: f.max_uses || null,
      max_uses_per_user: f.max_uses_per_user || null,
      valid_from: f.valid_from,
      valid_until: f.valid_until || null,
      active: f.active ? 1 : 0,
    }
    try {
      if (isEdit) await api.admin.discounts.update(id, payload)
      else        await api.admin.discounts.create(payload)
      navigate('/admin/discounts', { replace: true })
    } catch (err) {
      setError(err.code || 'server')
      setBusy(false)
    }
  }

  if (authLoading || loading) return <main className="container"><p>{t('common.loading')}</p></main>
  if (!user || user.role !== 'admin') {
    return <main className="container"><p className="error">{t('admin.errors.not_admin')}</p></main>
  }

  return (
    <main className="container">
      <p><Link to="/admin/discounts">← {t('admin_discounts.title')}</Link></p>
      <h1>{isEdit ? t('admin_discounts.edit_title') : t('admin_discounts.add_title')}</h1>

      {error && <p className="error">{t(`admin_discounts.errors.${error}`, {
        defaultValue: t(`admin.errors.${error}`, { defaultValue: error }),
      })}</p>}

      <section className="card form-card">
        <form onSubmit={submit} noValidate>
          <label>
            <span>{t('admin_discounts.form_name')}</span>
            <input value={f.name} onChange={set('name')} required minLength={2} />
          </label>

          <label>
            <span>{t('admin_discounts.form_type')}</span>
            <select value={f.type} onChange={set('type')}>
              <option value="percent">{t('admin_discounts.type_percent')}</option>
              <option value="fixed">{t('admin_discounts.type_fixed')}</option>
            </select>
          </label>

          <label>
            <span>{f.type === 'percent'
              ? t('admin_discounts.form_value_percent')
              : t('admin_discounts.form_value_fixed')}</span>
            <input type="number" min={1} max={f.type === 'percent' ? 100 : undefined} step={1}
                   value={f.value} onChange={set('value')} required />
            <small className="muted">{t('admin_discounts.form_value_hint')}</small>
          </label>

          <label>
            <span>{t('admin_discounts.form_code')}</span>
            <input value={f.code} onChange={(e) => setF((p) => ({ ...p, code: e.target.value.toUpperCase() }))}
                   autoComplete="off" />
            <small className="muted">{t('admin_discounts.form_code_hint')}</small>
          </label>

          <label>
            <span>{t('admin_discounts.form_applies_to')}</span>
            <select value={f.applies_to} onChange={set('applies_to')}>
              <option value="all">{t('admin_discounts.applies_all')}</option>
              <option value="one_off">{t('admin_discounts.applies_one_off')}</option>
              <option value="monthly">{t('admin_discounts.applies_monthly')}</option>
            </select>
          </label>

          <label>
            <span>{t('admin_discounts.form_course')}</span>
            <select value={f.course_id} onChange={set('course_id')}>
              <option value="">{t('admin_discounts.any')}</option>
              {courses.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>

          <label>
            <span>{t('admin_discounts.form_user')}</span>
            <select value={f.user_id} onChange={(e) =>
              setF((p) => ({ ...p, user_id: e.target.value, participant_id: '' }))}>
              <option value="">{t('admin_discounts.any')}</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>{u.name || u.email} — {u.email}</option>
              ))}
            </select>
          </label>

          <label>
            <span>{t('admin_discounts.form_participant')}</span>
            <select value={f.participant_id} onChange={set('participant_id')} disabled={!f.user_id}>
              <option value="">{t('admin_discounts.any')}</option>
              {participants.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <small className="muted">{t('admin_discounts.form_participant_hint')}</small>
          </label>

          <label>
            <span>{t('admin_discounts.form_valid_from')}</span>
            <input type="date" value={f.valid_from} onChange={set('valid_from')} required />
          </label>

          <label>
            <span>{t('admin_discounts.form_valid_until')}</span>
            <input type="date" value={f.valid_until} onChange={set('valid_until')} />
            <small className="muted">{t('admin_discounts.form_valid_until_hint')}</small>
          </label>

          <label>
            <span>{t('admin_discounts.form_max_uses')}</span>
            <input type="number" min={1} step={1} value={f.max_uses} onChange={set('max_uses')} />
            <small className="muted">{t('admin_discounts.form_unlimited_hint')}</small>
          </label>

          <label>
            <span>{t('admin_discounts.form_max_uses_per_user')}</span>
            <input type="number" min={1} step={1} value={f.max_uses_per_user}
                   onChange={set('max_uses_per_user')} />
            <small className="muted">{t('admin_discounts.form_unlimited_hint')}</small>
          </label>

          <label className="checkbox">
            <input type="checkbox" checked={f.active} onChange={set('active')} />
            <span>{t('admin_discounts.form_active')}</span>
          </label>

          <div className="form-actions">
            <button type="submit" disabled={busy}>{busy ? t('common.loading') : t('common.save')}</button>
            <Link to="/admin/discounts" className="link-button">{t('common.cancel')}</Link>
          </div>
        </form>
      </section>
    </main>
  )
}
